import { motion, AnimatePresence } from "framer-motion";
import { Banknote, Star } from "lucide-react";
import { UserAvatar } from "@/components/UserAvatar";
import { fmtTL } from "@/components/RevenueDialog";
import { MonthLeaderCard, WeekLeaderCard, MgrMonthLeaderCard, MgrWeekLeaderCard } from "@/components/Badges";

export function StarCard({ star, tv }) {
  if (!star) return null;
  return (
    <motion.div layout className="relative m-2 mb-0 rounded-xl border border-amber-400/50 bg-gradient-to-br from-amber-500/20 via-amber-500/5 to-transparent p-3 overflow-hidden" data-testid="star-card">
      <div className="flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-[0.2em] text-amber-200 mb-2">
        <Star size={12} className="fill-amber-300 text-amber-300" /> Günün Yıldızı
      </div>
      <div className="flex items-center gap-3">
        <div className="relative">
          <UserAvatar path={star.photo_path} name={star.name} className={tv ? "h-20 w-20" : "h-16 w-16"} rounded="rounded-2xl" />
          <span className="absolute -bottom-1 -right-1 h-6 w-6 rounded-full bg-amber-400 flex items-center justify-center shadow-lg">
            <Star size={13} className="fill-slate-900 text-slate-900" />
          </span>
        </div>
        <div className="min-w-0">
          <div className="font-display font-bold text-base leading-tight truncate" data-testid="star-name">{star.name}</div>
          <div className="text-[11px] text-slate-400 truncate">{star.branch_name}</div>
          {star.amount != null && <div className="font-display text-2xl font-black text-emerald-300 leading-tight" data-testid="star-amount">{fmtTL(star.amount)}</div>}
          <div className="text-[10px] text-slate-500">{star.count} adet satış</div>
        </div>
      </div>
    </motion.div>
  );
}

export function RevenueFeed({ rev, star, leader, weekLeader, mgrLeader, mgrWeekLeader, branches, filter, setFilter, tv }) {
  const feed = rev.feed.filter((x) => !filter || x.branch_id === filter);
  const today = filter ? rev.by_branch[filter]?.today || 0 : rev.today_total;
  const month = filter ? rev.by_branch[filter]?.month || 0 : rev.month_total;
  return (
    <div className={`${tv ? "w-64 h-full" : "w-full xl:w-72 h-[600px]"} shrink-0 rounded-xl border border-emerald-500/30 bg-slate-900/70 glass flex flex-col overflow-hidden`} data-testid="revenue-feed">
      <div className="px-3 py-3 border-b border-slate-800 space-y-2">
        <div className="flex items-center gap-2 font-display font-bold text-emerald-300"><Banknote size={16} /> Kazanç Akışı</div>
        <select value={filter} onChange={(e) => setFilter(e.target.value)} data-testid="revenue-filter"
                className="w-full h-8 rounded-md bg-slate-950/60 border border-slate-700 text-xs px-2">
          <option value="">Tüm şubeler</option>
          {branches.map((b) => <option key={b.branch_id} value={b.branch_id}>{b.branch_name}</option>)}
        </select>
      </div>
      <div className="flex-1 min-h-0 overflow-y-auto" data-testid="revenue-scroll">
      <MonthLeaderCard leader={leader} tv={tv} />
      <WeekLeaderCard leader={weekLeader} tv={tv} />
      <MgrMonthLeaderCard leader={mgrLeader} tv={tv} />
      <MgrWeekLeaderCard leader={mgrWeekLeader} tv={tv} />
      <StarCard star={star} tv={tv} />
      <div className="p-2 space-y-1.5 min-h-[120px]" data-testid="revenue-feed-list">
        <AnimatePresence initial={false}>
          {feed.map((x) => (
            <motion.div key={x.id} layout initial={{ opacity: 0, y: -16, scale: 0.95 }} animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0 }} transition={{ duration: 0.3 }}
                        className="rounded-lg border border-slate-800 bg-slate-950/50 px-2.5 py-2" data-testid={`revenue-item-${x.id}`}>
              <div className="flex items-center justify-between gap-2">
                <span className="text-[10px] font-mono uppercase text-blue-300 truncate">{x.branch_name}</span>
                <span className="font-display font-bold text-emerald-300 whitespace-nowrap">Ort: +{fmtTL(x.amount)}</span>
              </div>
              <div className="text-xs text-slate-300 truncate">{x.product_name} <span className="text-slate-500">×{x.quantity}</span></div>
              <div className="text-[10px] text-slate-500">{new Date(x.created_at).toLocaleTimeString("tr-TR", { hour: "2-digit", minute: "2-digit" })} · {x.user_name}</div>
            </motion.div>
          ))}
        </AnimatePresence>
        {feed.length === 0 && <div className="h-24 flex items-center justify-center text-xs text-slate-600">Bugün kazanç yok</div>}
      </div>
      </div>
      <div className="border-t border-slate-800 bg-emerald-500/5">
        <div className="px-3 py-2 flex items-center justify-between">
          <span className="text-[10px] font-mono uppercase text-emerald-200">Günlük Kazanç</span>
          <span className="font-display text-xl font-bold text-emerald-300" data-testid="revenue-today-total">{fmtTL(today)}</span>
        </div>
        <div className="px-3 py-2 flex items-center justify-between border-t border-slate-800/70">
          <span className="text-[10px] font-mono uppercase text-slate-400">Aylık Toplam</span>
          <span className="font-display text-lg font-bold text-slate-100" data-testid="revenue-month-total">{fmtTL(month)}</span>
        </div>
      </div>
    </div>
  );
}

export function TvStrip({ b }) {
  if (!b.monthly_target) {
    return <div className="mt-3 rounded-lg bg-slate-800/60 px-3 py-2 text-center text-xs text-slate-500" data-testid={`tv-strip-${b.branch_code}`}>Bu ay hedef tanımlanmamış</div>;
  }
  const f = b.forecast_hgo || 0;
  const cls = f >= 100 ? "bg-emerald-500/20 border-emerald-400/60 text-emerald-200" : f >= 80 ? "bg-amber-500/20 border-amber-400/60 text-amber-200" : "bg-red-500/20 border-red-400/60 text-red-200";
  return (
    <div className={`mt-3 grid grid-cols-2 rounded-xl border-2 ${cls}`} data-testid={`tv-strip-${b.branch_code}`}>
      <div className="px-3 py-2 text-center border-r border-current/20">
        <div className="text-[10px] font-mono uppercase tracking-widest opacity-80">Tahmini HGO</div>
        <div className="font-display text-4xl font-black leading-tight" data-testid={`tv-forecast-${b.branch_code}`}>%{f}</div>
      </div>
      <div className="px-3 py-2 text-center">
        <div className="text-[10px] font-mono uppercase tracking-widest opacity-80">Günlük Gereken</div>
        <div className="font-display text-4xl font-black leading-tight" data-testid={`tv-daily-${b.branch_code}`}>{b.daily_target > 0 ? fmtNumInt(b.daily_target) : "✓"}</div>
      </div>
    </div>
  );
}

const fmtNumInt = (n) => Number(n || 0).toLocaleString("tr-TR", { maximumFractionDigits: 0 });
